import {formatDateAsKey} from '@lib/utils';
import {ParkingReservationCountPerNodeByDate, ParkingReservationCountPerNode} from '../../data/entities';
import {getParkingNode} from '../../util/parkingNodes/treeHelpers';
import {GetParkingNodeInformationArgs, ParkingNodeInformation} from './types';
import {DayName, isDayClosed} from '../../data';
import {format} from 'date-fns';

const getParkingReservationCountPerNode = (
  date: Date,
  parkingCountPerNodeByDate: ParkingReservationCountPerNodeByDate,
): ParkingReservationCountPerNode => {
  const formattedDate = formatDateAsKey(date);
  return parkingCountPerNodeByDate[formattedDate];
};

const getParkingReservationCount = (nodeId: string, parkingsOnDate: ParkingReservationCountPerNode) =>
  parkingsOnDate[nodeId] || 0;

export const getParkingNodeInformation = ({
  nodeId,
  date,
  parkingLots,
  parkingZones,
  parkingCountPerNodeByDate,
}: GetParkingNodeInformationArgs): ParkingNodeInformation => {
  const formattedDate = format(date, 'EEEE') as DayName;
  const parkingNodeNotFoundResult = {occupation: 0, capacity: 0, isFull: false, isClosed: false};

  if (!nodeId || !date || !parkingCountPerNodeByDate) {
    return parkingNodeNotFoundResult;
  }

  const parkingNode = getParkingNode(nodeId, parkingLots, parkingZones);
  if (!parkingNode) {
    return parkingNodeNotFoundResult;
  }

  const {capacity} = parkingNode;

  const openingDays =
    ('parentId' in parkingNode
      ? parkingLots.find((parkingLot) => parkingLot.id === parkingNode.parentId)?.openingDays
      : parkingNode.openingDays) || [];

  const isClosed = isDayClosed(formattedDate, openingDays);

  if (isClosed) {
    return {
      occupation: capacity,
      capacity: capacity,
      isFull: true,
      isClosed: true,
    };
  }

  const parkingsOnDate = getParkingReservationCountPerNode(date, parkingCountPerNodeByDate) || {};
  const children = 'parkingZones' in parkingNode ? parkingNode.parkingZones : [];

  const occupation = getParkingReservationCount(nodeId, parkingsOnDate);
  const childrenOccupation = children.reduce((accumulatedOccupation, child) => {
    const childOccupation = getParkingReservationCount(child.id, parkingsOnDate);

    return (accumulatedOccupation += childOccupation);
  }, 0);

  const activeOccupation = childrenOccupation || occupation;

  return {
    occupation: activeOccupation,
    capacity: capacity,
    isFull: activeOccupation >= capacity,
    isClosed,
  };
};
